import React, { useState } from "react";
import { Button, Input } from "@fluentui/react-components";
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableColumnDefinition,
  createTableColumn,
} from "@fluentui/react-table";
import {
  AddRegular,
  DeleteRegular,
  EditRegular,
  PersonRegular,
} from "@fluentui/react-icons";
import "./styles.scss";
import LoadingWrapper from "#root/components/LoadingWrapper";
import Paginator from "#root/components/Paginator";
import { ROUTES } from "#root/routes/constants";
import { useNavigate } from "react-router-dom";
import { showToast } from "#root/components/GlobalToaster";
import { DialogBox } from "#root/components/DialogBox";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import {
  useSuperAdmins,
  useDeleteSuperAdmin,
  useUpdateSuperAdmin,
  SuperAdmin,
} from "#root/queries/superAdmin";

const SuperAdminUserManagement: React.FC = () => {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<SuperAdmin | null>(null);

  const [editingUser] = useState<SuperAdmin | null>(null);
  const [editedValues, setEditedValues] = useState<Partial<SuperAdmin>>({});

  const { data: usersResponse, isLoading, refetch } = useSuperAdmins({
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage,
  });

  const users = usersResponse?.items ?? [];
  const totalCount = usersResponse?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const updateSuperAdminMutation = useUpdateSuperAdmin();
  const deleteSuperAdminMutation = useDeleteSuperAdmin();

  const handleCreate = () => {
    navigate(ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_CREATE);
  };

  const handleEdit = (id: string) => {
    navigate(ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_EDIT.replace(':id', id));
  };

  const handleDeleteClick = (user: SuperAdmin, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };


const handleConfirmDelete = async () => {
  if (!userToDelete) return;
  try {
    await deleteSuperAdminMutation.mutateAsync({ id: userToDelete.id });
    showToast("SuperAdmin has been successfully deleted", "success");
    setUserToDelete(null);
    setIsDeleteDialogOpen(false);
    refetch();
  } catch (error) {
    showToast("Failed to delete Super Admin", "error");
  }
};

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    refetch();
  };


  const columns: TableColumnDefinition<SuperAdmin>[] = [
    createTableColumn<SuperAdmin>({
      columnId: "firstName",
      renderHeaderCell: () => "First Name",
      renderCell: (item) => {
        return editingUser?.id === item.id ? (
          <Input
            appearance="underline"
            value={editedValues.firstName || ""}
            onChange={(_, data) =>
              setEditedValues((prev) => ({ ...prev, firstName: data.value }))
            }
            disabled={updateSuperAdminMutation.isPending}
          />
        ) : (
          <div>{item.firstName}</div>
        );
      },
    }),
    createTableColumn<SuperAdmin>({
      columnId: "lastName",
      renderHeaderCell: () => "Last Name",
      renderCell: (item) => {
        return editingUser?.id === item.id ? (
          <Input
            appearance="underline"
            value={editedValues.lastName || ""}
            onChange={(_, data) =>
              setEditedValues((prev) => ({ ...prev, lastName: data.value }))
            }
            disabled={updateSuperAdminMutation.isPending}
          />
        ) : (
          <div>{item.lastName}</div>
        );
      },
    }),
    createTableColumn<SuperAdmin>({
      columnId: "email",
      renderHeaderCell: () => "Email",
      renderCell: (item) => {
        return editingUser?.id === item.id ? (
          <Input
            appearance="underline"
            value={editedValues.email || ""}
            onChange={(_, data) =>
              setEditedValues((prev) => ({ ...prev, email: data.value }))
            }
            disabled={updateSuperAdminMutation.isPending}
          />
        ) : (
          <div>{item.email}</div>
        );
      },
    }),
    createTableColumn<SuperAdmin>({
      columnId: "phoneNumber",
      renderHeaderCell: () => "Phone",
      renderCell: (item) => {
        return editingUser?.id === item.id ? (
          <Input
            appearance="underline"
            value={editedValues.phoneNumber || ""}
            onChange={(_, data) =>
              setEditedValues((prev) => ({ ...prev, phoneNumber: data.value }))
            }
            disabled={updateSuperAdminMutation.isPending}
          />
        ) : (
          <div>{item.phoneNumber}</div>
        );
      },
    }),
    createTableColumn<SuperAdmin>({
      columnId: "actions",
      renderHeaderCell: () => "Actions",
      renderCell: (item) => {
        return (
            <>
              <Button
                title="Edit"
                appearance="subtle"
                icon={<EditRegular />}
                onClick={() => handleEdit(item.id)}
                disabled={updateSuperAdminMutation.isPending}
              />
              <Button
                title="Delete"
                appearance="subtle"
                icon={<DeleteRegular />}
                onClick={(e) => handleDeleteClick(item, e)}
                disabled={deleteSuperAdminMutation.isPending}
              />
            </>
          );
      },
    }),
  ];

  return (
    <div className="users-administration">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>
            SuperAdmin User Management
            <div className="divider" />
            <Button appearance="primary" icon={<AddRegular />} onClick={handleCreate}>
              New Super Admin
            </Button>
          </h2>
        </div>
      </div>

      <LoadingWrapper
        isLoading={isLoading}
        isEmpty={users.length === 0}
        emptyMessage="No super admins found."
        emptyIcon={<PersonRegular />}
      >
        <div className="emc-grid">
          <DataGrid
            className="emc-datagrid"
            items={users}
            columns={columns}
            getRowId={(item) => item.id}
            focusMode="none"
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell, columnId }) => (
                  <DataGridHeaderCell className={String(columnId)}>
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<SuperAdmin>>
              {({ item, rowId }) => (
                <DataGridRow<SuperAdmin>
                  key={rowId}
                  className={editingUser?.id === item.id ? "editing" : ""}
                >
                  {({ renderCell, columnId }) => (
                    <DataGridCell className={String(columnId)}>
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>

          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalCount}
            currentItems={(currentPage - 1) * itemsPerPage + 1}
            showItemsPerPage={true}
          />
        </div>
      </LoadingWrapper>

      <DialogBox
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete User"
        primaryButtonText="Delete"
        onPrimaryButtonClick={handleConfirmDelete}
        isPrimaryButtonLoading={deleteSuperAdminMutation.isPending}
      >
        Permanently delete user {userToDelete?.email}? You can't undo this action.
      </DialogBox>

    </div>
  );
};

export default SuperAdminUserManagement;
